import type {TFunction} from "i18next";
import {SurveyType} from "crqlar-react-common/dist/models/survey/survey.model";

import {SURVEY_TYPE_LABEL_KEYS} from "../../../common/uiElements/SurveyTypeElement";
import {
    IdFilterKeys,
    IdFilterSelection,
    StoredIdFilter,
    readIdFilter,
    writeIdFilter
} from "./reservationTriggers";

/**
 * Survey trigger filters, the twin of reservationTriggers.ts. Contract mirrored by the API:
 * - `surveyIds` needs at least one entry, else the node never fires and activation is refused (`hasSurveyPick`).
 * - `guestCategoryIds` empty = any guest; else the guest matched one when invited, so anonymous responses never pass.
 * - SURVEY_SUBMITTED ignores submissions without an invitation; narrow by score with a Continue if step.
 * No "Any survey", survey-type, guest-type or response filter (AUTOMATIONS_UI.md, Decisions).
 */

/** Survey types label the picker's group headings, with the survey module's own keys. */
export const surveyTypeLabel = (type: string, t: TFunction): string => {
    const key = SURVEY_TYPE_LABEL_KEYS[type as SurveyType];
    return key ? t(key) : type;
};

export const DEFAULT_DAYS_AFTER_SENT = 3;

/** The API reads anything lower as this. */
export const MIN_DAYS_AFTER_SENT = 1;

/**
 * Mirrors `SurveyTriggerMatcher.daysAfterSent`: a number or numeric text, truncated, at least
 * MIN_DAYS_AFTER_SENT; anything else (a cleared field included) is the default.
 */
export const readDaysAfterSent = (config?: Record<string, any> | null): number => {
    const raw = config?.daysAfterSent;
    const stored = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() ? Number(raw) : NaN;
    const days = Number.isFinite(stored) ? Math.trunc(stored) : DEFAULT_DAYS_AFTER_SENT;
    return Math.max(days, MIN_DAYS_AFTER_SENT);
};

/** Every survey-trigger write goes through here so removed filter keys are stripped (the API ignores them). */
export const writeSurveyTriggerConfig = (
    config: Record<string, any>,
    patch: Record<string, any>
): Record<string, any> => ({
    ...config,
    ...patch,
    surveyTypes: undefined,
    guestTypes: undefined,
    includeAnonymous: undefined,
    responseMetric: undefined,
    responseClasses: undefined,
    scoreOperator: undefined,
    scoreValue: undefined,
    criticalOnly: undefined
});

export const SURVEY_FILTER_KEYS: IdFilterKeys = {
    ids: 'surveyIds', names: 'surveyNames', id: 'surveyId', name: 'surveyName'
};

export const readSurveyFilter = (config?: Record<string, any> | null): StoredIdFilter =>
    readIdFilter(config, SURVEY_FILTER_KEYS);

export const hasSurveyPick = (config?: Record<string, any> | null): boolean =>
    readSurveyFilter(config).ids.length > 0;

export const writeSurveyFilter = (selection: IdFilterSelection[]): Record<string, any> =>
    writeIdFilter(selection, SURVEY_FILTER_KEYS);

/** Nothing writes the singular pair; IdFilterKeys just requires it. */
export const GUEST_CATEGORY_FILTER_KEYS: IdFilterKeys = {
    ids: 'guestCategoryIds', names: 'guestCategoryNames', id: 'guestCategoryId', name: 'guestCategoryName'
};

export const readGuestCategoryFilter = (config?: Record<string, any> | null): StoredIdFilter =>
    readIdFilter(config, GUEST_CATEGORY_FILTER_KEYS);

export const writeGuestCategoryFilter = (selection: IdFilterSelection[]): Record<string, any> =>
    writeIdFilter(selection, GUEST_CATEGORY_FILTER_KEYS);

/** A nameless id still shows: a category the filter narrows on must never vanish from the list. */
export const formatGuestCategoryList = (filter: StoredIdFilter, t: TFunction): string =>
    filter.ids.map(id => filter.names[id] || t('Unavailable guest category')).join(', ');

/** Anonymous surveys have no invitations and no guest, so neither survey trigger nor Send survey offers them. */
export const isRemindableSurveyType = (type?: string): boolean => type !== SurveyType.ANONYMOUS;
